var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { showToast } from "@services/ToastService";
import { getUserName } from "@utils/auth";
import serviceApi from "./ApiInstance"; // Importing the pre-configured Axios instance
import { startLoading, stopLoading } from "./LoadingRegistry";
import { postData } from "./common/CommonService";
/**
 * API endpoint for user login.
 * @type {string}
 */
var LOGIN_API = "/useractions/login";
/**
 * Handles user login by sending an authentication request to the backend.
 * Sets the access and refresh tokens in session storage upon successful login.
 * Manages different error scenarios including network errors and existing sessions.
 *
 * @param {AuthRequest} authRequest - The user's authentication credentials (username and password).
 * @returns {Promise<AuthResponse>} A promise that resolves to an `AuthResponse` object,
 * containing an accessToken on success or an error message on failure.
 * @author niyas.shajahan
 * @since 2025-06-19
 */
export var login = function (authRequest) { return __awaiter(void 0, void 0, void 0, function () {
    var response, data, error_1;
    var _a;
    return __generator(this, function (_b) {
        switch (_b.label) {
            case 0:
                _b.trys.push([0, 3, , 4]);
                return [4 /*yield*/, logout(true)];
            case 1:
                _b.sent();
                return [4 /*yield*/, postData(LOGIN_API, authRequest)];
            case 2:
                response = _b.sent();
                data = response.responseBody;
                if (response.responseStatus === "SUCCESS" && data.accessToken) {
                    setToken(data); // Store tokens in session storage
                    sessionStorage.setItem("username", getUserName()); // Store username
                    return [2 /*return*/, data];
                }
                // If status is 200 but no accessToken, it might indicate a custom error
                return [2 /*return*/, {
                        error: data.error || "Unexpected login response",
                        accessToken: "",
                    }];
            case 3:
                error_1 = _b.sent();
                if (error_1.response) {
                    // Axios caught an HTTP error (like 400, 401, 500)
                    if (error_1.response.status === 400) {
                        // Return backend's error response directly for 400
                        return [2 /*return*/, error_1.response.data];
                    }
                    if (error_1.response.status === 429) {
                        return [2 /*return*/, {
                                error: "Too many login attempts. Please wait a minute and try again.",
                                accessToken: "",
                            }];
                    }
                    // For other HTTP errors, construct a generic error message
                    return [2 /*return*/, {
                            error: "Login failed: ".concat(((_a = error_1.response.data) === null || _a === void 0 ? void 0 : _a.message) || "Unknown error"),
                            accessToken: "",
                        }];
                }
                else {
                    // Network or other unexpected error
                    console.error("Login request failed:", error_1);
                    return [2 /*return*/, {
                            error: "Network error. Please try again.",
                            accessToken: "",
                        }];
                }
                return [3 /*break*/, 4];
            case 4: return [2 /*return*/];
        }
    });
}); };
/**
 * Stores the access token and refresh token in session storage.
 * @param {AuthResponse} tokenResponse - The authentication response containing access and refresh tokens.
 * @author niyas.shajahan
 * @since 2025-06-19
 */
var setToken = function (tokenResponse) {
    sessionStorage.setItem("token", tokenResponse.accessToken);
    sessionStorage.setItem("refreshToken", tokenResponse.refreshToken);
};
/**
 * Attempts to refresh the access token using the stored refresh token.
 * If refresh token is not available or refresh fails, it triggers a logout.
 * @async
 * @function refreshAccessToken
 * @returns {Promise<void>} A promise that resolves when refresh attempt is complete or rejects on failure.
 * @throws {Error} If no refresh token is available.
 * @author niyas.shajahan
 * @since 2025-06-19
 */
export function refreshAccessToken() {
    return __awaiter(this, void 0, void 0, function () {
        var refresh_token;
        return __generator(this, function (_a) {
            console.log("Attempting to refresh access token");
            refresh_token = sessionStorage.getItem("refreshToken");
            if (!refresh_token) {
                console.error("No refresh token available, logging out.");
                logout(); // Immediately log out if no refresh token
                throw new Error("No refresh token available");
            }
            try {
                // In a real application, you'd send refresh_token to an API endpoint to get a new access token
                // For this example, we'll simulate a failure and just call logout
                // const response = await serviceApi.post('/refresh-token', { refreshToken: refresh_token });
                // setToken(response.data);
                console.log("Simulating refresh token failure for demonstration.");
                logout(); // For demonstration, if refresh logic is not implemented, just logout
            }
            catch (error) {
                console.error("Failed to refresh access token:", error);
                logout(); // Ensure logout is called if refresh fails
            }
            return [2 /*return*/];
        });
    });
}
/**
 * Retrieves the stored access token from session storage.
 * @returns {string | null} The access token string, or `null` if not found.
 * @author niyas.shajahan
 * @since 2025-06-19
 */
export var getToken = function () {
    return sessionStorage.getItem("token");
};
/**
 * Logs out the user by clearing all session-related storage items
 * and redirecting to the login page. It also removes the Authorization header from Axios.
 * @async
 * @function logout
 * @returns {Promise<void>} A promise that resolves when logout process is complete.
 * @author niyas.shajahan
 * @since 2025-06-19
 */
export var logout = function (noRedirect) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        // Clear all session storage items related to authentication and user state
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("username");
        sessionStorage.removeItem("refreshToken");
        sessionStorage.removeItem("userInfo");
        // Remove Authorization header from Axios defaults to prevent sending expired tokens
        delete serviceApi.defaults.headers.common["Authorization"];
        if (noRedirect)
            return [2 /*return*/];
        // Redirect to the login page
        window.location.href = "/login";
        return [2 /*return*/];
    });
}); };
/**
 * Global variable to store the `navigate` function from `react-router-dom`.
 * This is used within Axios interceptors for programmatic navigation.
 * @type {NavigateFunction | null}
 */
var navigate = null;
/**
 * Sets the `navigate` function received from `react-router-dom` to a global variable.
 * This allows the Axios response interceptor to programmatically navigate.
 * @param {NavigateFunction} nav - The `navigate` function from `useNavigate` hook.
 * @author niyas.shajahan
 * @since 2025-06-19
 */
export var setInterceptorNavigate = function (nav) {
    navigate = nav;
};
/**
 * Axios request interceptor.
 * Intercepts outgoing requests to add the Authorization header with the Bearer token
 * if a token is available in session storage.
 * @param {import("axios").AxiosRequestConfig} config - The Axios request configuration.
 * @returns {import("axios").AxiosRequestConfig} The modified request configuration.
 * @author niyas.shajahan
 * @since 2025-06-19
 */
serviceApi.interceptors.request.use(function (config) {
    var token = getToken(); // Get token from session storage
    startLoading();
    if (token) {
        config.headers = __assign(__assign({}, config.headers), { Authorization: "Bearer ".concat(token) });
    }
    return config;
}, function (error) {
    stopLoading();
    return Promise.reject(error);
} // Reject request if an error occurs before sending
);
/**
 * Axios response interceptor.
 * Intercepts responses to handle common scenarios:
 * - Network errors: Navigates to an error page if a token exists.
 * - 401 Unauthorized errors: Attempts to refresh the access token and retries the original request.
 * If token refresh fails, it navigates to the login page.
 * @param {import("axios").AxiosResponse} response - The Axios response object.
 * @returns {import("axios").AxiosResponse | Promise<never>} The response object or a rejected promise.
 * @author niyas.shajahan
 * @since 2025-06-19
 */
serviceApi.interceptors.response.use(function (response) {
    stopLoading();
    return response;
}, // If response is successful, simply return it
function (error) { return __awaiter(void 0, void 0, void 0, function () {
    var _a, _b;
    return __generator(this, function (_c) {
        stopLoading();
        // Handle network errors (e.g., no internet connection)
        if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
            console.error("Network error:", error);
            // If a token exists, it means the user was logged in, so navigate to a network error page
            if (sessionStorage.getItem("token")) {
                // Ensure 'navigate' is available before calling it
                if (navigate)
                    navigate("/err");
            }
            return [2 /*return*/, Promise.reject(error)]; // Reject the promise to propagate the error
        }
        if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 401) {
            if (navigate) {
                navigate("/login");
            }
        }
        if (((_b = error.response) === null || _b === void 0 ? void 0 : _b.status) === 403) {
            showToast({
                severity: "error",
                summary: "Access Denied",
                detail: "You do not have permission to perform this action.",
                closable: true,
            });
        }
        return [2 /*return*/, Promise.reject(error)]; // For other errors, simply reject the promise
    });
}); });
// Export the configured Axios instance
export default serviceApi;
